/**
 * SchoolAlumniHub — extended alumni admin UI with 6 tabs:
 * Dashboard, Profiles, Chapters, Mentorship, Requests, Testimonials.
 *
 * Reuses the existing `GET /alumni` list endpoint for the profile picker.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  UserCheck, Users, Globe2, HeartHandshake, FileText, Star, Loader2,
  Plus, Trash2, RefreshCw, MapPin, Briefcase, IdCard, Mail, Sparkles, Edit2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const REQUEST_TYPES = [
  { value: 'transcript', label: 'Transcript' },
  { value: 'certificate_reprint', label: 'Certificate Reprint' },
  { value: 'recommendation', label: 'Recommendation Letter' },
  { value: 'reference', label: 'Reference Letter' },
  { value: 'verification_letter', label: 'Verification Letter' },
  { value: 'ministry_endorsement', label: 'Ministry Endorsement' },
];

const REQUEST_STATUS_COLOR = {
  submitted: 'bg-slate-100 text-slate-700',
  under_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-emerald-100 text-emerald-800',
  delivered: 'bg-emerald-200 text-emerald-900',
  rejected: 'bg-red-100 text-red-700',
};

export const SchoolAlumniHub = ({ tenantId }) => {
  const qc = useQueryClient();
  const [tab, setTab] = useState('dashboard');

  const { data: stats } = useQuery({ queryKey: ['alumni-dash', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-extended/dashboard`) });
  const { data: alumniData } = useQuery({ queryKey: ['alumni-list', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni?limit=100`) });
  const alumni = alumniData?.alumni || [];
  const { data: chapters = [] } = useQuery({ queryKey: ['chapters', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-chapters`) });
  const { data: mentorships = [] } = useQuery({ queryKey: ['mentorships', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-mentorships`) });
  const { data: requests = [] } = useQuery({ queryKey: ['alumni-reqs', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-requests`) });
  const { data: testimonials = [] } = useQuery({ queryKey: ['testimonials', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-testimonials`) });

  const autoConvert = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni/auto-convert`, { method: 'POST' }),
    onSuccess: r => { toast.success(`Created ${r.created} new alumni profiles`); qc.invalidateQueries({ queryKey: ['alumni-dash'] }); },
    onError: e => toast.error(e.message),
  });

  return (
    <div className="space-y-4" data-testid="school-alumni-hub">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><UserCheck className="h-5 w-5 text-indigo-600" /> Alumni Hub</h2>
          <p className="text-xs text-slate-500">Lifelong relationship centre — profiles, chapters, mentorship, giving, requests.</p>
        </div>
        <Button size="sm" onClick={() => autoConvert.mutate()} disabled={autoConvert.isPending} variant="outline" data-testid="auto-convert-btn">
          {autoConvert.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Sync graduates → alumni
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="dashboard" data-testid="alumni-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="profiles" data-testid="alumni-tab-profiles">Profiles</TabsTrigger>
          <TabsTrigger value="chapters" data-testid="alumni-tab-chapters">Chapters</TabsTrigger>
          <TabsTrigger value="mentorship" data-testid="alumni-tab-mentorship">Mentorship</TabsTrigger>
          <TabsTrigger value="requests" data-testid="alumni-tab-requests">Requests</TabsTrigger>
          <TabsTrigger value="giving" data-testid="alumni-tab-giving">Giving</TabsTrigger>
          <TabsTrigger value="events" data-testid="alumni-tab-events">Events</TabsTrigger>
          <TabsTrigger value="businesses" data-testid="alumni-tab-businesses">Businesses</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="alumni-tab-newsletter">Newsletter</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="alumni-tab-testimonials">Stories</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Total Alumni', value: stats?.total_alumni ?? 0, color: 'text-indigo-600', bg: 'bg-indigo-50' },
              { label: 'New This Year', value: stats?.new_graduates_this_year ?? 0, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'International', value: stats?.international_count ?? 0, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Active Mentors', value: stats?.available_mentors ?? 0, color: 'text-purple-600', bg: 'bg-purple-50' },
              { label: 'Active Chapters', value: stats?.active_chapters ?? 0, color: 'text-amber-600', bg: 'bg-amber-50' },
              { label: 'Active Mentorships', value: stats?.active_mentorships ?? 0, color: 'text-pink-600', bg: 'bg-pink-50' },
              { label: 'Pending Requests', value: stats?.pending_requests ?? 0, color: 'text-red-600', bg: 'bg-red-50' },
              { label: 'Featured Stories', value: stats?.featured_testimonials ?? 0, color: 'text-slate-600', bg: 'bg-slate-50' },
              { label: 'Alumni Giving', value: `$${(stats?.alumni_giving_total ?? 0).toLocaleString()}`, color: 'text-green-700', bg: 'bg-green-50' },
              { label: 'Alumni Events', value: stats?.upcoming_alumni_events ?? 0, color: 'text-cyan-700', bg: 'bg-cyan-50' },
            ].map((s, i) => (
              <Card key={i} className={s.bg}><CardContent className="p-4">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-600 mt-1">{s.label}</p>
              </CardContent></Card>
            ))}
          </div>
          {(stats?.employment_breakdown || []).length > 0 && (
            <Card className="mt-4">
              <CardHeader className="pb-2"><CardTitle className="text-base">Where our alumni are serving</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {stats.employment_breakdown.map((e) => (
                  <Badge key={e.status} variant="outline" className="text-sm py-1 px-3">
                    <span className="capitalize">{e.status.replace('_', ' ')}</span>
                    <span className="ml-2 font-bold">{e.count}</span>
                  </Badge>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="profiles" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Admission #</TableHead><TableHead>Graduated</TableHead><TableHead>Last Class</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {alumni.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-400">No alumni yet. Click <strong>Sync graduates</strong> above.</TableCell></TableRow>}
                {alumni.slice(0, 30).map(a => (
                  <TableRow key={a.id} data-testid={`alumni-profile-${a.id}`}>
                    <TableCell className="font-medium">{a.first_name} {a.last_name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{a.admission_number}</TableCell>
                    <TableCell className="text-xs text-slate-500">{a.graduation_date || '—'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{a.last_class || '—'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <ProfileEditButton tenantId={tenantId} alumniId={a.id} name={`${a.first_name} ${a.last_name}`} />
                        <AlumniCardButton tenantId={tenantId} alumniId={a.id} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="chapters" className="mt-4">
          <ChaptersManager tenantId={tenantId} chapters={chapters} alumni={alumni} />
        </TabsContent>

        <TabsContent value="mentorship" className="mt-4">
          <MentorshipManager tenantId={tenantId} mentorships={mentorships} alumni={alumni} />
        </TabsContent>

        <TabsContent value="requests" className="mt-4">
          <RequestsManager tenantId={tenantId} requests={requests} alumni={alumni} />
        </TabsContent>

        <TabsContent value="testimonials" className="mt-4">
          <TestimonialsManager tenantId={tenantId} testimonials={testimonials} alumni={alumni} />
        </TabsContent>

        <TabsContent value="giving" className="mt-4">
          <GivingManager tenantId={tenantId} alumni={alumni} />
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <AlumniEventsManager tenantId={tenantId} chapters={chapters} />
        </TabsContent>

        <TabsContent value="businesses" className="mt-4">
          <BusinessesManager tenantId={tenantId} alumni={alumni} />
        </TabsContent>

        <TabsContent value="newsletter" className="mt-4">
          <NewsletterManager tenantId={tenantId} chapters={chapters} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ---------- Profile Edit Dialog ----------

function ProfileEditButton({ tenantId, alumniId, name }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: profile } = useQuery({ queryKey: ['profile', alumniId], queryFn: () => api(`/tenants/${tenantId}/alumni-profiles/${alumniId}`), enabled: open });
  const [form, setForm] = useState({});
  React.useEffect(() => { if (profile) setForm(profile); }, [profile]);

  const saveMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni-profiles/${alumniId}`, { method: 'PUT', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Profile updated'); qc.invalidateQueries({ queryKey: ['profile', alumniId] }); qc.invalidateQueries({ queryKey: ['alumni-dash'] }); setOpen(false); },
    onError: e => toast.error(e.message),
  });

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`edit-profile-${alumniId}`}>Edit Profile</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Alumni Profile — {name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Phone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label className="text-xs">Email</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label className="text-xs">Country</Label><Input value={form.country || ''} onChange={e => setForm({ ...form, country: e.target.value })} /></div>
            <div><Label className="text-xs">City</Label><Input value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs">Employment Status</Label>
              <Select value={form.employment_status || ''} onValueChange={v => setForm({ ...form, employment_status: v })}>
                <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{['employed', 'self_employed', 'ministry', 'student', 'seeking'].map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Employer / Organization</Label><Input value={form.employer || ''} onChange={e => setForm({ ...form, employer: e.target.value })} /></div>
            <div><Label className="text-xs">Position / Title</Label><Input value={form.position || ''} onChange={e => setForm({ ...form, position: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs">Ministry Placement</Label><Input value={form.ministry_placement || ''} onChange={e => setForm({ ...form, ministry_placement: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs">Profile Summary / Testimony</Label><Textarea rows={3} value={form.profile_summary || ''} onChange={e => setForm({ ...form, profile_summary: e.target.value })} /></div>
            <div className="flex items-center gap-2"><input type="checkbox" id="mentor" checked={!!form.available_for_mentorship} onChange={e => setForm({ ...form, available_for_mentorship: e.target.checked })} /><Label htmlFor="mentor" className="text-xs">Available as mentor</Label></div>
            <div className="flex items-center gap-2"><input type="checkbox" id="support" checked={form.willing_to_support !== false} onChange={e => setForm({ ...form, willing_to_support: e.target.checked })} /><Label htmlFor="support" className="text-xs">Willing to support</Label></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>{saveMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------- Chapters ----------

function ChaptersManager({ tenantId, chapters, alumni }) {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'country', country: '', city: '', description: '', is_active: true });
  const createMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni-chapters`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Chapter created'); qc.invalidateQueries({ queryKey: ['chapters'] }); setShow(false); setForm({ name: '', type: 'country', country: '', city: '', description: '', is_active: true }); },
    onError: e => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/alumni-chapters/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['chapters'] }); },
  });

  return (
    <>
      <div className="flex justify-end mb-3"><Button size="sm" onClick={() => setShow(true)} data-testid="new-chapter-btn"><Plus className="h-3.5 w-3.5 mr-1" />New Chapter</Button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {chapters.length === 0 && <p className="col-span-full text-center text-slate-400 py-10">No chapters yet. Start a UK Chapter, Pastors Fellowship, or Class of 2024 group.</p>}
        {chapters.map(c => (
          <Card key={c.id} data-testid={`chapter-${c.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Globe2 className="h-4 w-4 text-indigo-500" />{c.name}</CardTitle>
                <Badge variant="outline" className="text-[10px] capitalize">{c.type}</Badge>
              </div>
              <CardDescription className="text-xs"><MapPin className="inline h-3 w-3 mr-0.5" />{[c.city, c.country].filter(Boolean).join(', ') || 'Global'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-slate-500 line-clamp-2">{c.description}</p>
              <p className="text-xs font-medium">{c.member_count || 0} member{c.member_count === 1 ? '' : 's'}</p>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setDetailId(c.id)} data-testid={`chapter-view-${c.id}`}>View</Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { if (window.confirm('Delete chapter?')) delMut.mutate(c.id); }}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Chapter</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="UK Alumni Chapter" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['country', 'city', 'region', 'international', 'professional', 'ministry', 'year'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Country</Label><Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMut.mutate()} disabled={!form.name || createMut.isPending} data-testid="save-chapter-btn">{createMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {detailId && <ChapterDetailDialog tenantId={tenantId} chapterId={detailId} alumni={alumni} onClose={() => setDetailId(null)} />}
    </>
  );
}

// Chapter detail: members + roles + upcoming chapter events
function ChapterDetailDialog({ tenantId, chapterId, alumni, onClose }) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['chapter-detail', chapterId],
    queryFn: () => api(`/tenants/${tenantId}/alumni-chapters/${chapterId}/detail`),
  });
  const [addId, setAddId] = useState('');
  const addMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni-chapters/${chapterId}/members`, { method: 'POST', body: JSON.stringify({ alumni_id: addId, chapter_role: 'member' }) }),
    onSuccess: () => { toast.success('Member added'); setAddId(''); refetch(); qc.invalidateQueries({ queryKey: ['chapters'] }); },
    onError: e => toast.error(e.message),
  });
  const roleMut = useMutation({
    mutationFn: ({ memberId, role }) => api(`/tenants/${tenantId}/alumni-chapters/${chapterId}/members/${memberId}/role`, { method: 'PUT', body: JSON.stringify({ chapter_role: role }) }),
    onSuccess: () => { toast.success('Role updated'); refetch(); },
    onError: e => toast.error(e.message),
  });
  const rmMut = useMutation({
    mutationFn: memberId => api(`/tenants/${tenantId}/alumni-chapters/${chapterId}/members/${memberId}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); refetch(); qc.invalidateQueries({ queryKey: ['chapters'] }); },
  });
  const ch = data?.chapter || {};
  const members = data?.members || [];
  const roles = ['member', 'committee', 'chair', 'vice_chair', 'treasurer', 'secretary'];
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="chapter-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Globe2 className="h-5 w-5 text-indigo-500" />{ch.name || 'Chapter'}</DialogTitle>
          <DialogDescription className="text-xs">{[ch.city, ch.country].filter(Boolean).join(', ') || 'Global'} · {ch.type}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {(data?.role_breakdown || []).map(r => (
                <Badge key={r.role} variant="outline" className="text-[10px]"><span className="capitalize">{r.role.replace('_', ' ')}</span> <strong className="ml-1">{r.count}</strong></Badge>
              ))}
              <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Total <strong className="ml-1">{data?.member_count || 0}</strong></Badge>
            </div>
            <div className="flex gap-2">
              <Select value={addId} onValueChange={setAddId}>
                <SelectTrigger data-testid="chapter-add-alumni-select"><SelectValue placeholder="Add alumnus as member…" /></SelectTrigger>
                <SelectContent>{alumni.slice(0, 50).map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" disabled={!addId || addMut.isPending} onClick={() => addMut.mutate()} data-testid="chapter-add-member-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
            </div>
            <Card><CardContent className="p-0 max-h-[300px] overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Member</TableHead><TableHead>Role</TableHead><TableHead>Joined</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {members.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">No members yet.</TableCell></TableRow>}
                  {members.map(m => (
                    <TableRow key={m.id} data-testid={`chapter-member-${m.id}`}>
                      <TableCell className="text-sm">{m.name || m.alumni_id}<div className="text-[10px] text-slate-400">{[m.city, m.country].filter(Boolean).join(', ')}</div></TableCell>
                      <TableCell>
                        <Select value={m.chapter_role || 'member'} onValueChange={role => roleMut.mutate({ memberId: m.id, role })}>
                          <SelectTrigger className="h-7 text-xs w-full max-w-[130px]"><SelectValue /></SelectTrigger>
                          <SelectContent>{roles.map(r => <SelectItem key={r} value={r} className="text-xs capitalize">{r.replace('_', ' ')}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{m.joined_at ? new Date(m.joined_at).toLocaleDateString() : ''}</TableCell>
                      <TableCell><Button size="sm" variant="ghost" className="text-red-500" onClick={() => rmMut.mutate(m.id)}><Trash2 className="h-3 w-3" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
            {(data?.events || []).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-600 mb-1">Chapter events</h4>
                <ul className="space-y-1 text-xs">
                  {data.events.map(e => <li key={e.id}>📅 {e.title} <span className="text-slate-400">{e.start_date?.slice(0, 10)}</span></li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MentorshipManager({ tenantId, mentorships, alumni }) {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [sessionFor, setSessionFor] = useState(null);
  const [findMentor, setFindMentor] = useState(false);
  const [autoMatch, setAutoMatch] = useState(false);
  const [form, setForm] = useState({ mentor_alumni_id: '', mentee_id: '', mentee_type: 'student', focus_area: '', goals: '' });
  const { data: analytics } = useQuery({ queryKey: ['mentorship-analytics', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-mentorships/analytics`) });
  const createMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni-mentorships`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Mentorship created'); qc.invalidateQueries({ queryKey: ['mentorships'] }); qc.invalidateQueries({ queryKey: ['mentorship-analytics'] }); setShow(false); },
    onError: e => toast.error(e.message),
  });
  const closeMut = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/alumni-mentorships/${id}/close`, { method: 'POST' }),
    onSuccess: () => { toast.success('Closed'); qc.invalidateQueries({ queryKey: ['mentorships'] }); qc.invalidateQueries({ queryKey: ['mentorship-analytics'] }); },
  });

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="mentorship-analytics">
        {[
          { label: 'Active', value: analytics?.active ?? 0, color: 'text-pink-600', bg: 'bg-pink-50' },
          { label: 'Closed', value: analytics?.closed ?? 0, color: 'text-slate-600', bg: 'bg-slate-50' },
          { label: 'Available Mentors', value: analytics?.available_mentors ?? 0, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Total Sessions', value: analytics?.total_sessions ?? 0, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Avg / pair', value: analytics?.avg_sessions_per_pair ?? 0, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} rounded-lg p-2.5`}>
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-slate-600 uppercase tracking-wide">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mb-3">
        <Button size="sm" variant="outline" onClick={() => setAutoMatch(true)} data-testid="auto-match-btn"><Sparkles className="h-3.5 w-3.5 mr-1" />Auto-Match Student</Button>
        <Button size="sm" variant="outline" onClick={() => setFindMentor(true)} data-testid="find-mentor-btn"><HeartHandshake className="h-3.5 w-3.5 mr-1" />Find Mentors</Button>
        <Button size="sm" onClick={() => setShow(true)} data-testid="new-mentorship-btn"><Plus className="h-3.5 w-3.5 mr-1" />New Mentorship</Button>
      </div>
      <Card><CardContent className="p-0"><Table>
        <TableHeader><TableRow><TableHead>Mentor</TableHead><TableHead>Mentee</TableHead><TableHead>Focus</TableHead><TableHead>Sessions</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {mentorships.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No active mentorships yet.</TableCell></TableRow>}
          {mentorships.map(m => (
            <TableRow key={m.id} data-testid={`mentorship-${m.id}`}>
              <TableCell className="text-sm">{m.mentor_name || m.mentor_alumni_id}</TableCell>
              <TableCell className="text-xs text-slate-500">{m.mentee_name || m.mentee_id}</TableCell>
              <TableCell className="text-xs">{m.focus_area || '—'}</TableCell>
              <TableCell>{(m.sessions || []).length}</TableCell>
              <TableCell><Badge variant="outline" className="capitalize text-[10px]">{m.status}</Badge></TableCell>
              <TableCell>
                <div className="flex gap-1">
                  {m.status === 'active' && <Button size="sm" variant="outline" onClick={() => setSessionFor(m)} data-testid={`log-session-${m.id}`}>Log Session</Button>}
                  {m.status === 'active' && <Button size="sm" variant="ghost" onClick={() => closeMut.mutate(m.id)}>Close</Button>}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></CardContent></Card>

      {(analytics?.focus_areas || []).length > 0 && (
        <Card className="mt-3"><CardHeader className="pb-2"><CardTitle className="text-sm">Top focus areas</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {analytics.focus_areas.map(f => (
              <Badge key={f.area} variant="outline" className="text-xs"><span>{f.area}</span> <strong className="ml-1">{f.count}</strong></Badge>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Mentorship</DialogTitle>
            <DialogDescription className="text-xs">Pair an alumni mentor with a student or fellow alumnus.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Mentor</Label>
              <Select value={form.mentor_alumni_id} onValueChange={v => setForm({ ...form, mentor_alumni_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choose alumnus…" /></SelectTrigger>
                <SelectContent>{alumni.slice(0, 50).map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Mentee</Label>
              <Select value={form.mentee_id} onValueChange={v => setForm({ ...form, mentee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choose alumnus…" /></SelectTrigger>
                <SelectContent>{alumni.slice(0, 50).map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Focus Area</Label><Input value={form.focus_area} onChange={e => setForm({ ...form, focus_area: e.target.value })} placeholder="Church planting, leadership, coding…" /></div>
            <div><Label className="text-xs">Goals</Label><Textarea rows={2} value={form.goals} onChange={e => setForm({ ...form, goals: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMut.mutate()} disabled={!form.mentor_alumni_id || !form.mentee_id || createMut.isPending} data-testid="save-mentorship-btn">{createMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sessionFor && <LogSessionDialog tenantId={tenantId} mentorship={sessionFor} onClose={() => setSessionFor(null)} />}
      {findMentor && <FindMentorDialog tenantId={tenantId} onClose={() => setFindMentor(false)} />}
      {autoMatch && <AutoMatchDialog tenantId={tenantId} alumni={alumni} onClose={() => setAutoMatch(false)} onCreate={(form) => { setForm(form); setAutoMatch(false); setShow(true); }} />}
    </>
  );
}

function LogSessionDialog({ tenantId, mentorship, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), duration_minutes: 60, notes: '', feedback: '' });
  const mut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni-mentorships/${mentorship.id}/sessions`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Session logged'); qc.invalidateQueries({ queryKey: ['mentorships'] }); qc.invalidateQueries({ queryKey: ['mentorship-analytics'] }); onClose(); },
    onError: e => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent data-testid="log-session-dialog">
        <DialogHeader>
          <DialogTitle>Log Mentorship Session</DialogTitle>
          <DialogDescription className="text-xs">{mentorship.mentor_name} → {mentorship.mentee_name || mentorship.mentee_id}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label className="text-xs">Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: Number(e.target.value) })} /></div>
          </div>
          <div><Label className="text-xs">Notes</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Topics discussed, progress, next steps…" /></div>
          <div><Label className="text-xs">Feedback</Label><Textarea rows={2} value={form.feedback} onChange={e => setForm({ ...form, feedback: e.target.value })} placeholder="Mentee feedback / rating" /></div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="save-session-btn">{mut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FindMentorDialog({ tenantId, onClose }) {
  const [filters, setFilters] = useState({ focus_area: '', country: '' });
  const { data: suggestions = [], refetch, isFetching } = useQuery({
    queryKey: ['mentor-suggestions', tenantId, filters],
    queryFn: () => {
      const q = new URLSearchParams();
      if (filters.focus_area) q.append('focus_area', filters.focus_area);
      if (filters.country) q.append('country', filters.country);
      return api(`/tenants/${tenantId}/alumni-mentorship-suggestions?${q.toString()}`);
    },
  });
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="find-mentor-dialog">
        <DialogHeader>
          <DialogTitle>Find Available Mentors</DialogTitle>
          <DialogDescription className="text-xs">Filter alumni who are available for mentorship, ranked by current mentee load.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Focus area / expertise</Label><Input value={filters.focus_area} onChange={e => setFilters({ ...filters, focus_area: e.target.value })} placeholder="leadership, coding…" /></div>
          <div><Label className="text-xs">Country</Label><Input value={filters.country} onChange={e => setFilters({ ...filters, country: e.target.value })} placeholder="UK, USA…" /></div>
        </div>
        <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching} data-testid="refresh-mentor-suggestions">{isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}Search</Button></div>
        <div className="max-h-[380px] overflow-y-auto space-y-2">
          {suggestions.length === 0 && !isFetching && <p className="text-center text-slate-400 text-sm py-6">No mentors match these filters. Encourage alumni to enable "Available for mentorship" in their profile.</p>}
          {suggestions.map(s => (
            <Card key={s.alumni_id} data-testid={`mentor-suggestion-${s.alumni_id}`}>
              <CardContent className="p-3 flex items-start gap-3">
                <div className="h-9 w-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold">{(s.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}</div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-slate-500">{[s.position, s.employer].filter(Boolean).join(' · ') || '—'}</p>
                  <p className="text-[10px] text-slate-400">{[s.city, s.country].filter(Boolean).join(', ')}</p>
                  {(s.areas_of_expertise || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {s.areas_of_expertise.slice(0, 4).map(a => <Badge key={a} variant="outline" className="text-[10px]">{a}</Badge>)}
                    </div>
                  )}
                </div>
                <Badge className="bg-slate-100 text-slate-700 text-[10px]">{s.active_mentees} active</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AutoMatchDialog({ tenantId, alumni, onClose, onCreate }) {
  const [studentId, setStudentId] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { data: studentsData } = useQuery({
    queryKey: ['students-for-automatch', tenantId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/students?limit=200`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const d = await res.json().catch(() => ({}));
      return d.students || [];
    },
  });
  const students = (studentsData || []).filter(s => s.status !== 'graduated');
  const runMatch = async () => {
    setIsLoading(true);
    try {
      const r = await api(`/tenants/${tenantId}/alumni-mentorship-auto-match?student_id=${studentId}`);
      setResult(r);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="auto-match-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" />Auto-Match Mentor</DialogTitle>
          <DialogDescription className="text-xs">Pick a student; we'll rank available alumni mentors by program fit + expertise + mentee load.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Select value={studentId} onValueChange={setStudentId}>
            <SelectTrigger data-testid="auto-match-student-select"><SelectValue placeholder="Choose student…" /></SelectTrigger>
            <SelectContent>{students.slice(0, 100).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.program || s.department || 'General'}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={runMatch} disabled={!studentId || isLoading} data-testid="run-auto-match-btn">{isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}Match</Button>
        </div>
        {result && (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            <p className="text-xs text-slate-500">Suggestions for <strong>{result.student_name}</strong> ({result.student_program || 'General'}):</p>
            {result.suggestions.length === 0 && <p className="text-center text-slate-400 text-sm py-4">No available mentors yet. Encourage alumni to enable "Available for mentorship".</p>}
            {result.suggestions.map((s, i) => (
              <Card key={s.alumni_id} data-testid={`match-${s.alumni_id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="text-xs font-bold text-slate-400 w-4">#{i + 1}</div>
                  <div className="h-9 w-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold">{(s.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{s.name}</p>
                    <p className="text-[11px] text-slate-500">{[s.position, s.employer].filter(Boolean).join(' · ') || '—'} · {s.country || 'N/A'}</p>
                    {(s.areas_of_expertise || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1">{s.areas_of_expertise.slice(0, 3).map(a => <Badge key={a} variant="outline" className="text-[9px]">{a}</Badge>)}</div>}
                  </div>
                  <div className="text-right space-y-1">
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Score {s.score}</Badge>
                    <p className="text-[10px] text-slate-400">{s.active_mentees} active</p>
                    <Button size="sm" variant="outline" onClick={() => onCreate({ mentor_alumni_id: s.alumni_id, mentee_id: result.student_id, mentee_type: 'student', focus_area: result.student_program, goals: '' })} data-testid={`pair-${s.alumni_id}`}>Pair →</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ---------- Requests ----------

function RequestsManager({ tenantId, requests, alumni }) {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({ alumni_id: '', request_type: 'transcript', details: '', payment_required: false, amount: 0 });
  const { data: stats } = useQuery({ queryKey: ['alumni-requests-stats', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-requests/stats`) });
  const createMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni-requests`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Request submitted'); qc.invalidateQueries({ queryKey: ['alumni-reqs'] }); qc.invalidateQueries({ queryKey: ['alumni-requests-stats'] }); setShow(false); },
    onError: e => toast.error(e.message),
  });
  const transitionMut = useMutation({
    mutationFn: ({ id, status }) => api(`/tenants/${tenantId}/alumni-requests/${id}/transition`, { method: 'POST', body: JSON.stringify({ status }) }),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries({ queryKey: ['alumni-reqs'] }); qc.invalidateQueries({ queryKey: ['alumni-requests-stats'] }); },
    onError: e => toast.error(e.message),
  });

  const nextStatus = (s) => ({ submitted: 'under_review', under_review: 'approved', approved: 'completed', completed: 'delivered' }[s]);

  const filters = ['all', 'submitted', 'under_review', 'approved', 'completed', 'delivered', 'rejected', 'overdue'];
  const filtered = requests.filter(r => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'overdue') return r.is_overdue;
    return r.status === statusFilter;
  });

  return (
    <>
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="requests-stats">
        {[
          { label: 'Total', value: stats?.total ?? 0, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Overdue', value: stats?.overdue ?? 0, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Pending', value: (stats?.by_status || []).filter(s => ['submitted', 'under_review'].includes(s.status)).reduce((a, b) => a + b.count, 0), color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Delivered', value: stats?.delivered_count ?? 0, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Avg resolution (d)', value: stats?.avg_resolution_days ?? 0, color: 'text-indigo-600', bg: 'bg-indigo-50' },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} rounded-lg p-2.5`}>
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-slate-600 uppercase tracking-wide">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-1 mb-3" data-testid="requests-filter-bar">
        {filters.map(f => (
          <Button key={f} size="sm" variant={statusFilter === f ? 'default' : 'outline'} onClick={() => setStatusFilter(f)} data-testid={`filter-${f}`} className="h-7 text-[11px] capitalize">
            {f.replace('_', ' ')}{f === 'overdue' && stats?.overdue > 0 && <span className="ml-1 bg-red-500 text-white rounded-full px-1 text-[9px]">{stats.overdue}</span>}
          </Button>
        ))}
        <div className="flex-1" />
        <Button size="sm" onClick={() => setShow(true)} data-testid="new-request-btn"><Plus className="h-3.5 w-3.5 mr-1" />New Request</Button>
      </div>

      <Card><CardContent className="p-0"><Table>
        <TableHeader><TableRow><TableHead>Alumni</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>SLA</TableHead><TableHead>Assigned</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No requests match this filter.</TableCell></TableRow>}
          {filtered.map(r => (
            <TableRow key={r.id} data-testid={`request-${r.id}`} className={r.is_overdue ? 'bg-red-50/50' : ''}>
              <TableCell className="text-sm">{r.alumni_name || r.alumni_id}</TableCell>
              <TableCell className="text-xs capitalize">{r.request_type.replace(/_/g, ' ')}</TableCell>
              <TableCell><Badge className={REQUEST_STATUS_COLOR[r.status] || ''}>{r.status.replace('_', ' ')}</Badge></TableCell>
              <TableCell>
                {r.is_overdue
                  ? <Badge className="bg-red-100 text-red-700 text-[10px]">Overdue · {r.days_open}d</Badge>
                  : <span className="text-xs text-slate-500">{r.days_open ?? 0}d / {r.sla_threshold ?? 7}d</span>}
              </TableCell>
              <TableCell className="text-xs text-slate-500">{r.assigned_to_name || '—'}</TableCell>
              <TableCell>
                <div className="flex gap-1 flex-wrap">
                  {nextStatus(r.status) && (
                    <Button size="sm" variant="outline" onClick={() => transitionMut.mutate({ id: r.id, status: nextStatus(r.status) })} data-testid={`next-${r.id}`}>
                      → {nextStatus(r.status).replace('_', ' ')}
                    </Button>
                  )}
                  {['submitted', 'under_review', 'approved'].includes(r.status) && (
                    <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => setAssignFor(r)} data-testid={`assign-${r.id}`}>Assign</Button>
                  )}
                  {['submitted', 'under_review', 'approved'].includes(r.status) && (
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => transitionMut.mutate({ id: r.id, status: 'rejected' })}>Reject</Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></CardContent></Card>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Alumni Request</DialogTitle>
            <DialogDescription className="text-xs">Log a request on behalf of an alumnus (transcript, reference, letter, etc).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Alumni</Label>
              <Select value={form.alumni_id} onValueChange={v => setForm({ ...form, alumni_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choose alumnus…" /></SelectTrigger>
                <SelectContent>{alumni.slice(0, 50).map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Request Type</Label>
              <Select value={form.request_type} onValueChange={v => setForm({ ...form, request_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{REQUEST_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Details</Label><Textarea rows={3} value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMut.mutate()} disabled={!form.alumni_id || createMut.isPending} data-testid="save-request-btn">{createMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {assignFor && <AssignRequestDialog tenantId={tenantId} request={assignFor} onClose={() => setAssignFor(null)} />}
    </>
  );
}

function AssignRequestDialog({ tenantId, request, onClose }) {
  const qc = useQueryClient();
  const [name, setName] = useState(request.assigned_to_name || '');
  const mut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni-requests/${request.id}/assign`, { method: 'POST', body: JSON.stringify({ assigned_to_name: name }) }),
    onSuccess: () => { toast.success('Assigned'); qc.invalidateQueries({ queryKey: ['alumni-reqs'] }); onClose(); },
    onError: e => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent data-testid="assign-request-dialog">
        <DialogHeader>
          <DialogTitle>Assign Request</DialogTitle>
          <DialogDescription className="text-xs">Assign this {request.request_type?.replace(/_/g, ' ')} request to a staff member.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Staff Member Name</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Registrar, Dean's office…" /></div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => mut.mutate()} disabled={!name.trim() || mut.isPending} data-testid="save-assign-btn">{mut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Testimonials ----------

function TestimonialsManager({ tenantId, testimonials, alumni }) {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ alumni_id: '', title: '', body: '', media_type: 'text', is_featured: false, is_public: false });
  const createMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni-testimonials`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Testimonial added'); qc.invalidateQueries({ queryKey: ['testimonials'] }); setShow(false); },
    onError: e => toast.error(e.message),
  });

  return (
    <>
      <div className="flex justify-end mb-3"><Button size="sm" onClick={() => setShow(true)} data-testid="new-testimonial-btn"><Star className="h-3.5 w-3.5 mr-1" />Add Story</Button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {testimonials.length === 0 && <p className="col-span-full text-center text-slate-400 py-10">No testimonials yet.</p>}
        {testimonials.map(t => (
          <Card key={t.id} data-testid={`testimonial-${t.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">{t.is_featured && <Star className="h-4 w-4 text-amber-500" />}{t.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 line-clamp-4">{t.body}</p>
              {t.is_public && <Badge variant="outline" className="mt-2 text-[10px]">Public</Badge>}
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Success Story</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Alumni</Label>
              <Select value={form.alumni_id} onValueChange={v => setForm({ ...form, alumni_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{alumni.slice(0, 50).map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label className="text-xs">Body</Label><Textarea rows={5} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} /></div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.is_featured} onChange={e => setForm({ ...form, is_featured: e.target.checked })} />Featured</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.is_public} onChange={e => setForm({ ...form, is_public: e.target.checked })} />Public (show on website)</label>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMut.mutate()} disabled={!form.alumni_id || !form.title || createMut.isPending}>{createMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SchoolAlumniHub;

// ---------- Giving ----------

function GivingManager({ tenantId, alumni }) {
  const [selected, setSelected] = useState('');
  const { data: summary } = useQuery({ queryKey: ['giving-summary', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-giving/summary`) });
  const { data: giving } = useQuery({
    queryKey: ['giving', selected],
    queryFn: () => api(`/tenants/${tenantId}/alumni/${selected}/giving`),
    enabled: !!selected,
  });

  return (
    <div className="space-y-3" data-testid="giving-manager">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-green-50"><CardContent className="p-4"><p className="text-2xl font-bold text-green-700">${(summary?.total_paid ?? 0).toLocaleString()}</p><p className="text-xs text-slate-600 mt-1">Total Paid</p></CardContent></Card>
        <Card className="bg-emerald-50"><CardContent className="p-4"><p className="text-2xl font-bold text-emerald-700">${(summary?.total_pledged ?? 0).toLocaleString()}</p><p className="text-xs text-slate-600 mt-1">Pledged</p></CardContent></Card>
        <Card className="bg-blue-50"><CardContent className="p-4"><p className="text-2xl font-bold text-blue-700">${(summary?.total_donations ?? 0).toLocaleString()}</p><p className="text-xs text-slate-600 mt-1">Direct Donations</p></CardContent></Card>
        <Card className="bg-purple-50"><CardContent className="p-4"><p className="text-2xl font-bold text-purple-700">{summary?.giver_count ?? 0}</p><p className="text-xs text-slate-600 mt-1">Alumni Givers</p></CardContent></Card>
      </div>

      {(summary?.top_givers || []).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Top Alumni Givers</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table><TableBody>
              {summary.top_givers.map((g, i) => (
                <TableRow key={g.email}>
                  <TableCell className="w-10 text-center font-bold text-slate-400">{i + 1}</TableCell>
                  <TableCell className="font-medium text-sm">{g.name}</TableCell>
                  <TableCell className="text-right font-semibold text-green-700">${g.total.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody></Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Individual giving history</CardTitle>
          <CardDescription className="text-xs">Select an alumnus to view their pledges & donations (matched via email / phone on file).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="giving-alumni-select"><SelectValue placeholder="Choose alumnus…" /></SelectTrigger>
            <SelectContent>{alumni.slice(0, 100).map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent>
          </Select>
          {giving && (
            <>
              <div className="flex gap-2 flex-wrap text-sm">
                <Badge variant="outline">Paid: <strong className="ml-1">${giving.totals.paid.toLocaleString()}</strong></Badge>
                <Badge variant="outline">Pledged: <strong className="ml-1">${giving.totals.pledged.toLocaleString()}</strong></Badge>
                <Badge variant="outline">Donations: <strong className="ml-1">${giving.totals.donations.toLocaleString()}</strong></Badge>
                <Badge variant="outline">Records: <strong className="ml-1">{giving.totals.count}</strong></Badge>
              </div>
              {giving.pledges?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-600 mb-1">Pledges</p>
                  <div className="space-y-1">
                    {giving.pledges.slice(0, 10).map(p => (
                      <div key={p.id} className="flex justify-between text-xs border-b py-1.5">
                        <span>{p.campaign_name || p.notes || 'Pledge'}</span>
                        <span className="font-medium">${p.amount} <span className="text-slate-400">(paid ${p.paid_amount || 0})</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {giving.totals.count === 0 && <p className="text-sm text-slate-400 py-3 text-center">No giving records matched on this alumnus' email/phone.</p>}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Alumni Events ----------

function AlumniEventsManager({ tenantId, chapters }) {
  const qc = useQueryClient();
  const { data: events = [] } = useQuery({ queryKey: ['alumni-events', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-events`) });
  const { data: allEvents = [] } = useQuery({
    queryKey: ['all-events'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/events`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) return [];
      const d = await res.json();
      return d.events || d || [];
    },
  });

  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ event_id: '', alumni_only: false, chapter_id: '' });
  const tagMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni-events/tag`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Event tagged as alumni event'); qc.invalidateQueries({ queryKey: ['alumni-events'] }); qc.invalidateQueries({ queryKey: ['alumni-dash'] }); setShow(false); },
    onError: e => toast.error(e.message),
  });

  return (
    <div className="space-y-3" data-testid="alumni-events-manager">
      <div className="flex justify-end"><Button size="sm" onClick={() => setShow(true)} data-testid="tag-alumni-event-btn"><Plus className="h-3.5 w-3.5 mr-1" />Tag Event as Alumni</Button></div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Event</TableHead><TableHead>When</TableHead><TableHead>Scope</TableHead><TableHead>Registered</TableHead></TableRow></TableHeader>
          <TableBody>
            {events.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-400">No alumni events yet. Tag an existing event or create one in the Events module and come back.</TableCell></TableRow>}
            {events.map(e => (
              <TableRow key={e.id} data-testid={`alumni-event-${e.id}`}>
                <TableCell className="font-medium">{e.title}</TableCell>
                <TableCell className="text-xs text-slate-500">{e.start_date?.slice(0, 10)}</TableCell>
                <TableCell>
                  {e.alumni_only ? <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Alumni only</Badge> : <Badge variant="outline" className="text-[10px]">Open</Badge>}
                  {e.alumni_chapter_id && <Badge variant="outline" className="ml-1 text-[10px]">Chapter</Badge>}
                </TableCell>
                <TableCell className="text-sm">{e.total_registrations || 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tag Event as Alumni Event</DialogTitle>
            <DialogDescription className="text-xs">Mark an existing event as an alumni event. Optionally link it to a chapter or hide it from non-alumni.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Event</Label>
              <Select value={form.event_id} onValueChange={v => setForm({ ...form, event_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{(allEvents || []).slice(0, 50).map(e => <SelectItem key={e.id || e._id} value={e.id || e._id}>{e.title || e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Chapter (optional)</Label>
              <Select value={form.chapter_id || 'none'} onValueChange={v => setForm({ ...form, chapter_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No chapter —</SelectItem>
                  {chapters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.alumni_only} onChange={e => setForm({ ...form, alumni_only: e.target.checked })} />Alumni-only (hide from non-alumni)</label>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => tagMut.mutate()} disabled={!form.event_id || tagMut.isPending} data-testid="save-tag-event-btn">{tagMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Tag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------- Phase 4: Digital Alumni Card Button ----------
function AlumniCardButton({ tenantId, alumniId }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['alumni-card', alumniId],
    queryFn: () => api(`/tenants/${tenantId}/alumni/${alumniId}/card`),
    enabled: open,
  });
  const verifyUrl = data ? `${window.location.origin}${data.verify_path}` : '';
  const qrImg = verifyUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(verifyUrl)}` : '';
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`alumni-card-btn-${alumniId}`}><IdCard className="h-3 w-3 mr-1" />Card</Button>
      {open && (
        <Dialog open onOpenChange={v => { if (!v) setOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="alumni-card-dialog">
            <DialogHeader>
              <DialogTitle>Digital Alumni Card</DialogTitle>
              <DialogDescription className="text-xs">Printable / shareable credential with QR verification.</DialogDescription>
            </DialogHeader>
            {isLoading || !data ? (
              <div className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : (
              <div className="rounded-xl overflow-hidden border shadow-lg" id={`card-${alumniId}`}>
                <div className="p-4 text-white" style={{ background: `linear-gradient(135deg, ${data.institution_color}, ${data.institution_color}dd)` }}>
                  <div className="flex items-center gap-3">
                    {data.institution_logo && <img src={data.institution_logo} alt="logo" className="h-10 w-10 rounded bg-white/20 object-contain" />}
                    <div className="flex-1">
                      <p className="text-[10px] uppercase tracking-wider opacity-80">Alumni Card</p>
                      <p className="text-sm font-bold">{data.institution}</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-white">
                  <div className="flex gap-3">
                    <div className="h-16 w-16 rounded bg-slate-100 flex items-center justify-center text-slate-400 font-bold">
                      {data.photo_url ? <img src={data.photo_url} alt="" className="h-full w-full object-cover rounded" /> : (data.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{data.name}</p>
                      <p className="text-xs text-slate-500">{data.program || '—'} · Class of {data.graduation_year || '—'}</p>
                      <p className="text-[11px] text-slate-400 mt-1"><strong>ID:</strong> {data.admission_number}</p>
                      {(data.position || data.employer) && <p className="text-[11px] text-slate-500">{[data.position, data.employer].filter(Boolean).join(' · ')}</p>}
                    </div>
                  </div>
                  <div className="mt-3 flex items-end justify-between">
                    <div className="text-[9px] text-slate-400 max-w-[160px]">
                      <p>Verify at:</p>
                      <p className="break-all">{verifyUrl}</p>
                    </div>
                    {qrImg && <img src={qrImg} alt="QR" className="h-[90px] w-[90px]" />}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
              <Button onClick={() => window.print()} data-testid="print-alumni-card">Print</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ---------- Phase 4: Businesses Manager ----------
function BusinessesManager({ tenantId, alumni }) {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ alumni_id: '', name: '', category: 'service', description: '', website: '', email: '', phone: '', country: '', city: '', logo_url: '', is_public: true, is_featured: false });
  const { data: businesses = [] } = useQuery({ queryKey: ['alumni-businesses', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-businesses`) });
  const mut = useMutation({
    mutationFn: () => editing
      ? api(`/tenants/${tenantId}/alumni-businesses/${editing}`, { method: 'PUT', body: JSON.stringify(form) })
      : api(`/tenants/${tenantId}/alumni-businesses`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success(editing ? 'Updated' : 'Business added'); qc.invalidateQueries({ queryKey: ['alumni-businesses'] }); setShow(false); setEditing(null); setForm({ alumni_id: '', name: '', category: 'service', description: '', website: '', email: '', phone: '', country: '', city: '', logo_url: '', is_public: true, is_featured: false }); },
    onError: e => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/alumni-businesses/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['alumni-businesses'] }); },
  });
  const CATEGORIES = ['ministry', 'consulting', 'product', 'service', 'nonprofit', 'retail', 'tech', 'other'];

  return (
    <div data-testid="businesses-manager">
      <div className="flex justify-between items-center mb-3">
        <p className="text-xs text-slate-500">Directory of businesses & ministries owned or led by your alumni.</p>
        <Button size="sm" onClick={() => setShow(true)} data-testid="new-business-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add Business</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {businesses.length === 0 && <p className="col-span-full text-center text-slate-400 py-10">No businesses listed yet. Invite alumni to share their ventures.</p>}
        {businesses.map(b => (
          <Card key={b.id} data-testid={`business-${b.id}`} className={b.is_featured ? 'ring-2 ring-amber-300' : ''}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                {b.logo_url ? <img src={b.logo_url} alt="" className="h-8 w-8 rounded" /> : <Briefcase className="h-7 w-7 text-indigo-500 p-1 bg-indigo-50 rounded" />}
                <div className="flex-1">
                  <CardTitle className="text-sm">{b.name} {b.is_featured && <Star className="h-3 w-3 text-amber-500 inline" />}</CardTitle>
                  <CardDescription className="text-[10px] capitalize">{b.category} · by {b.alumni_name || '—'}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5">
              <p className="text-xs text-slate-600 line-clamp-2">{b.description || '—'}</p>
              <p className="text-[10px] text-slate-400"><MapPin className="inline h-3 w-3 mr-0.5" />{[b.city, b.country].filter(Boolean).join(', ') || 'Global'}</p>
              {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-600 hover:underline break-all">{b.website}</a>}
              <div className="flex gap-1 pt-1">
                <Button size="sm" variant="outline" onClick={() => { setEditing(b.id); setForm({ ...b }); setShow(true); }} data-testid={`edit-business-${b.id}`}><Edit2 className="h-3 w-3" /></Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { if (window.confirm('Delete this business?')) delMut.mutate(b.id); }}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={show} onOpenChange={v => { setShow(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Business' : 'Add Alumni Business'}</DialogTitle>
            <DialogDescription className="text-xs">List a venture, ministry or consulting practice led by one of your alumni.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Alumni Owner</Label>
              <Select value={form.alumni_id} onValueChange={v => setForm({ ...form, alumni_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choose alumnus…" /></SelectTrigger>
                <SelectContent>{alumni.slice(0, 50).map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Business Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Description</Label><Textarea rows={2} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label className="text-xs">Website</Label><Input value={form.website || ''} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://…" /></div>
            <div><Label className="text-xs">Email</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label className="text-xs">Country</Label><Input value={form.country || ''} onChange={e => setForm({ ...form, country: e.target.value })} /></div>
            <div><Label className="text-xs">City</Label><Input value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
            <div className="col-span-2 flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.is_public !== false} onChange={e => setForm({ ...form, is_public: e.target.checked })} />Public in directory</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!form.is_featured} onChange={e => setForm({ ...form, is_featured: e.target.checked })} />Featured</label>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => mut.mutate()} disabled={!form.alumni_id || !form.name || mut.isPending} data-testid="save-business-btn">{mut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}{editing ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Phase 4: Newsletter Manager ----------
function NewsletterManager({ tenantId, chapters }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ subject: '', body_html: '', filter_country: '', filter_chapter_id: '', filter_graduation_year: '', featured_only: false });
  const { data: history = [] } = useQuery({ queryKey: ['alumni-newsletters', tenantId], queryFn: () => api(`/tenants/${tenantId}/alumni-newsletters`) });
  const sendMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/alumni-newsletters`, { method: 'POST', body: JSON.stringify({ ...form, filter_chapter_id: form.filter_chapter_id === 'all' ? '' : form.filter_chapter_id }) }),
    onSuccess: r => {
      toast.success(`Sent to ${r.sent_count} / ${r.recipient_count} alumni${r.email_configured ? '' : ' (email NOT configured — record saved only)'}`);
      qc.invalidateQueries({ queryKey: ['alumni-newsletters'] });
      setForm({ subject: '', body_html: '', filter_country: '', filter_chapter_id: '', filter_graduation_year: '', featured_only: false });
    },
    onError: e => toast.error(e.message),
  });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="newsletter-manager">
      <Card className="lg:col-span-3">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" />Compose Newsletter</CardTitle><CardDescription className="text-xs">Use <code>{'{{name}}'}</code> to personalise the greeting.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div><Label className="text-xs">Subject</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Homecoming 2026 is here!" data-testid="newsletter-subject" /></div>
          <div><Label className="text-xs">Body (HTML supported)</Label><Textarea rows={8} value={form.body_html} onChange={e => setForm({ ...form, body_html: e.target.value })} placeholder="<p>Dear {{name}},</p><p>We're excited to invite you…</p>" data-testid="newsletter-body" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div><Label className="text-xs">Country (opt)</Label><Input value={form.filter_country} onChange={e => setForm({ ...form, filter_country: e.target.value })} /></div>
            <div><Label className="text-xs">Grad Year (opt)</Label><Input value={form.filter_graduation_year} onChange={e => setForm({ ...form, filter_graduation_year: e.target.value })} placeholder="2024" /></div>
            <div><Label className="text-xs">Chapter (opt)</Label>
              <Select value={form.filter_chapter_id || 'all'} onValueChange={v => setForm({ ...form, filter_chapter_id: v === 'all' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All chapters</SelectItem>
                  {chapters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.featured_only} onChange={e => setForm({ ...form, featured_only: e.target.checked })} />Featured alumni only (donors / partners)</label>
          <Button onClick={() => sendMut.mutate()} disabled={!form.subject || !form.body_html || sendMut.isPending} data-testid="send-newsletter-btn">{sendMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}Send Newsletter</Button>
        </CardContent>
      </Card>
      <Card className="lg:col-span-2">
        <CardHeader className="pb-2"><CardTitle className="text-base">Recent sends</CardTitle></CardHeader>
        <CardContent className="space-y-2 max-h-[450px] overflow-y-auto">
          {history.length === 0 && <p className="text-center text-slate-400 text-sm py-6">No newsletters sent yet.</p>}
          {history.map(n => (
            <div key={n.id} className="border rounded p-2 text-xs" data-testid={`newsletter-history-${n.id}`}>
              <p className="font-medium truncate">{n.subject}</p>
              <p className="text-slate-500">{n.sent_count}/{n.recipient_count} · {n.failed_count ? <span className="text-red-500">{n.failed_count} failed</span> : 'ok'}</p>
              <p className="text-[10px] text-slate-400">{n.sent_at ? new Date(n.sent_at).toLocaleString() : ''}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
